import { CogIcon, CollectionIcon, UserIcon } from "@heroicons/react/outline";
import {
  ActivityIcon,
  BarChart3,
  ClipboardList,
  Filter,
  GitBranch,
  InboxIcon,
  KanbanIcon,
  LibraryIcon,
  LifeBuoy,
  type LucideProps,
  Megaphone,
  QrCode,
} from "lucide-react";
import React from "react";
import type { FeatureFlag } from "@/lib/feature-flags/constants";

export interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current?: boolean;
  count?: number;
  /**
   * When set, the item is only shown if the given PostHog feature flag is
   * enabled for the current user.
   */
  featureFlag?: FeatureFlag;
  /**
   * When set, the item is hidden while the given PostHog feature flag is
   * enabled: the inverse of `featureFlag`, for items a rollout replaces.
   */
  hiddenWhenFeatureFlag?: FeatureFlag;
  /**
   * Extra path prefixes that mark this item current, in addition to `href`.
   * Used when one nav item owns several workspace routes (Calls, Tasks).
   */
  currentPrefixes?: string[];
  /**
   * Shown in the nav for upcoming destinations. Renders as a non-link so it
   * cannot 404.
   */
  placeholder?: boolean;
  /**
   * Pulsing callout on the nav item (see `Hotspot` in
   * `@/components/walkthrough`) for drawing attention to a newly added or
   * reworked destination. Dismissal persists per browser under `id`. When the
   * callout introduces a new destination, that screen should call
   * `markWalkthroughSeen(id)` on mount so arriving retires the dot; when it
   * explains a change to a screen people already visit, leave it to "Got it"
   * so the explanation is read at least once.
   *
   * `anyFeatureFlag` shows the callout only while at least one of the listed
   * flags is enabled, for callouts about flag-gated changes to an item that
   * is itself always shown. `hiddenWhenFeatureFlag` suppresses it while that
   * flag is on, so a gym with a bigger callout elsewhere in the nav sees one
   * dot, not two.
   */
  hotspot?: {
    id: string;
    title: string;
    body: string;
    anyFeatureFlag?: FeatureFlag[];
    hiddenWhenFeatureFlag?: FeatureFlag;
  };
}

export interface NavigationGroup {
  title: string;
  items: NavigationItem[];
}

// Forms icon wrapper with custom props
const FormsIcon = (props: LucideProps) => (
  <ClipboardList {...props} strokeWidth={1.75} absoluteStrokeWidth />
);

export const sidebarNavigationGroups: NavigationGroup[] = [
  {
    title: "ACTIVITY",
    items: [
      {
        name: "Inbox",
        href: "/messages",
        icon: InboxIcon,
        currentPrefixes: ["/calls", "/tasks"],
        hotspot: {
          id: "inbox-nav",
          title: "Your messages live in Inbox",
          body: "Inbox is where you text and email customers. Calls and Tasks are now unified in the Inbox as well.",
          anyFeatureFlag: ["helios-web-calling", "helios-tasks"],
          hiddenWhenFeatureFlag: "helios-pulse",
        },
      },
      {
        name: "Pulse",
        href: "/pulse",
        icon: ActivityIcon,
        featureFlag: "helios-pulse",
        hotspot: {
          id: "pulse-nav",
          title: "Start your day in Pulse",
          body: "Pulse is your daily list of who to reach and why, with a message drafted for each. Stages shows the same people as a board, one column per step of their journey. Inbox still holds your texts, emails, calls, and tasks.",
        },
      },
      {
        name: "Stages",
        href: "/stages",
        icon: KanbanIcon,
        featureFlag: "helios-stages",
      },
    ],
  },
  // {
  //   title: 'VOICE',
  //   items: [
  //     {
  //       name: 'Call Forwarding',
  //       href: '/settings/voice/call-forwarding',
  //       icon: PhoneForwarded,
  //     },
  //     {
  //       name: 'Voicemail',
  //       href: '/settings/voice/voicemail',
  //       icon: Voicemail,
  //     },
  //   ],
  // },
  {
    title: "MARKETING",
    items: [
      {
        name: "Blasts",
        href: "/blasts",
        icon: Megaphone,
      },
      {
        name: "Flows",
        href: "/automations",
        icon: GitBranch,
      },
      {
        name: "Segments",
        href: "/lists",
        icon: Filter,
      },
      {
        name: "Contacts",
        href: "/contacts",
        icon: UserIcon,
      },
      {
        name: "Templates",
        href: "/templates",
        icon: LibraryIcon,
      },
      {
        name: "Forms",
        href: "/forms",
        icon: FormsIcon,
        hiddenWhenFeatureFlag: "helios-pages",
      },
      {
        name: "Pages",
        href: "/pages",
        icon: CollectionIcon,
        featureFlag: "helios-pages",
        hotspot: {
          id: "pages-nav",
          title: "Build landing pages",
          body: "Pages holds your forms and landing pages: build, share, and track them from here.",
        },
      },
      {
        name: "QR Codes",
        href: "/qr-codes",
        icon: QrCode,
        featureFlag: "helios-qr-codes",
      },
    ],
  },
  {
    title: "ANALYTICS",
    items: [
      {
        name: "Reports",
        href: "/analytics/overview",
        icon: BarChart3,
      },
    ],
  },
];

export interface SubNavigationItem {
  id?: string;
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  current?: boolean;
  onClick?: () => void;
}

export const sidebarSubNavigation: Array<Omit<SubNavigationItem, "current">> = [
  {
    name: "Settings",
    href: "/settings",
    icon: CogIcon,
  },
  {
    id: "help-support",
    name: "Help / Feedback",
    href: "#",
    icon: LifeBuoy,
  },
];
